import { useRef, useState, useEffect } from 'react'
import {ThunkDispatch} from "@reduxjs/toolkit";
import {useSelector, useDispatch} from 'react-redux'
import { useNavigate } from "react-router-dom"
import LoginHeader from "./LoginHeader"
import {currentUserActions} from '../reducers/currentUser'
import LogoHeader from "./LogoHeader";
import TextInput from './TextInput';
import CustomButton from './CustomButton';
import CheckboxButton from "../components/CheckboxButton";

const CodeLength = 6

interface LoginSubmitCodeProps {
  view: 'company' | 'talent';
  onCancel?: () => void;
}

const LoginSubmitCode = ({view, onCancel} : LoginSubmitCodeProps) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const currentUser = useSelector((state: any) => state.currentUser)
  const navigate = useNavigate()
  const {error, isLoading} = currentUser
  const [code, setCode] = useState('');
  const isSubmitDisabled = code.length != CodeLength || isLoading
  const [remember, setRemember] = useState(false);

  // Refs to control each digit input element
  const inputRefs = [
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
  ] as any

  // Handle input
  function handleInput(e: any, index: number) {
      const input = e.target;
      const previousInput = inputRefs[index - 1]
      const nextInput = inputRefs[index + 1];

      // Update code state with single digit
      const newCode = [...code];
      // Convert lowercase letters to uppercase
      if (/^[a-z]+$/.test(input.value)) {
          const uc = input.value.toUpperCase();
          newCode[index] = uc;
          inputRefs[index].current.value = uc;
      } else {
          newCode[index] = input.value;
      }
      setCode(newCode.join(''));

      if (input.value === '') {
          // If the value is deleted, select previous input, if exists
          if (previousInput) {
              previousInput.current.focus();
          }
      } else if (nextInput) {
          // Select next input on entry, if exists
          nextInput.current.select();
      }
  }

  // Select the contents on focus
  function handleFocus(e: any) {
      e.target.select();
  }

  // Handle backspace key
  function handleKeyDown(e: any, index: number) {
      const input = e.target;
      const previousInput = inputRefs[index - 1];
      const nextInput = inputRefs[index + 1];

      if ((e.keyCode === 8 || e.keyCode === 46) && input.value === '') {
          e.preventDefault();
          setCode((prevCode) => prevCode.slice(0, index) + prevCode.slice(index + 1));
          if (previousInput) {
              previousInput.current.focus();
          }
      }
  }

  // Capture pasted characters
  const handlePaste = (e: any) => {
      const pastedCode = e.clipboardData.getData('text');
      if (pastedCode.length === 6) {
          setCode(pastedCode);
          inputRefs.forEach((inputRef: any, index: number) => {
              inputRef.current.value = pastedCode.charAt(index);
          });
      }
  };

  const signInUser = (event: any) => {
    event.preventDefault();

    if (isSubmitDisabled) return
    dispatch(currentUserActions.signInUser({auth_code: code, navigate, view, remember}))
  };

  const clearForm = () => {
    if (onCancel) {
      onCancel();
    } else {
      dispatch(currentUserActions.setInitState());
    }
  };

  return (
    <div className='static md:fixed md:inset-0 bg-alu-base'>
      <div className='min-w-[24.75rem] static md:absolute md:top-1/2 left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2'>
        <LogoHeader view={view} page="advanced"></LogoHeader>
        <div className='bg-white md:rounded-2xl md:shadow-lg md:max-w-[24.375rem] md:m-auto md:min-h-max min-h-screen w-full py-0 md:py-6'>
          <LoginHeader title="2段階認証コードを入力してログイン"></LoginHeader>
          <div className="flex flex-col items-center justify-center mt-[3.125rem] md:mt-4 px-4 md:px-6">
            <form onSubmit={signInUser} className="bg-white w-full">
              <div className="mb-6">
                <p className='text-sm mb-2 text-center text-[#808080]'>メールに表示されている6桁のコードを入力</p>

                <div className='flex gap-2 justify-center max-w-full'>
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <TextInput
                      customClassName="text-xl text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      key={index}
                      maxLength={1}
                      onChange={(e:any) => handleInput(e, index)}
                      ref={inputRefs[index]}
                      autoFocus={index === 0}
                      onFocus={handleFocus}
                      onKeyDown={(e:any) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  ))}
                </div>
                {error && (
                  <p className='alu-text-error text-sm mt-2'>＊{error}</p>
                )}
              </div>

              <div className="mb-2">
                <CheckboxButton
                  name="remember"
                  label="このブラウザでは次回からコードを要求しない"
                  value={remember}
                  onChange={() => {setRemember(!remember)}}
                  iconSize="20"
                  customStyle="!gap-1"
                />
              </div>
              
              <CustomButton
                type="submit"
                width='full'
                customStyle='mb-6'
                disabled={isSubmitDisabled}
              >
                ログイン
              </CustomButton>
              <div className="w-full flex justify-center">
                <CustomButton
                  variant='text'
                  onClick={clearForm}
                >
                  キャンセルして戻る
                </CustomButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSubmitCode;
